import { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useAuth } from "src/context/AuthContext";
import { useT } from "src/i18n";
import { useCurrentPerson } from "src/utils/useCurrentPerson";
import { listMeAsiakases } from "src/utils/quote/quoteApi";
import { captureError } from "src/utils/errorReporting";
import PersonEditDialog from "src/components/PersonEditDialog";
import GlassDialog from "src/components/common/GlassDialog";

// Account-menu "Omat tiedot" entry point. Reuses the shared PersonEditDialog
// (the same form the wizard uses) so the user can edit their own person fields
// + private billing address from anywhere they're logged in.
//
// PersonEditDialog seeds its form in a useState initializer at mount, so the
// billing prefill must be present before it mounts. We therefore fetch the
// caller's billable asiakases first, derive the private (typeId=2) row, and
// show a spinner in the meantime — then mount PersonEditDialog with the row in
// hand. The parent mounts this only while open (`{profileOpen && <...>}`) so
// `loading` seeds true on each open and the form re-seeds, matching the same
// conditional-mount contract PersonEditDialog/AsiakasEditDialog rely on.
export default function MyDetailsDialog({ onClose }) {
    const { token } = useAuth();
    const t = useT();
    const person = useCurrentPerson();
    const [privateAsiakas, setPrivateAsiakas] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        listMeAsiakases({ token })
            .then((list) => {
                if (cancelled) return;
                const rows = Array.isArray(list) ? list : [];
                setPrivateAsiakas(rows.find((a) => a.asiakasTypeId === 2) || null);
            })
            .catch((err) => {
                if (cancelled) return;
                // Non-fatal: person fields stay editable, billing just starts blank.
                // 401 means the token expired — RequireAuth/AuthContext handle logout.
                if (err?.status !== 401) {
                    captureError(err, { tags: { entity: "me-asiakases", operation: "list", swallowed: true } });
                }
                setPrivateAsiakas(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [token]);

    if (loading) {
        return (
            <GlassDialog open onClose={onClose} title={t("common.nav.myDetails")}>
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            </GlassDialog>
        );
    }

    return (
        <PersonEditDialog
            open
            onClose={onClose}
            person={person}
            privateAsiakas={privateAsiakas}
            siteAddress={null}
            onSaved={(result) => {
                if (result?.privateAsiakas) setPrivateAsiakas(result.privateAsiakas);
            }}
        />
    );
}
